import { useCallback, useEffect, useRef, useState } from "react"
import { Change } from "../../types"
import { hasBrowserEventListeners } from "../../utils/browserEvent"
import { useActiveWorkspacePage } from "./common/useActiveWorkspacePage"

export type PreviewVariant = {
  experiment_id: string
  variant_id: string
  changes: Partial<Change>[]
  activation_event?: string | null
  css?: string | null
  js?: string | null
};

export type PreviewMessage = {
  type: string
  data: PreviewVariant
  action?: string
  requestId?: string
};

type PreviewAckMessage = {
  type: 'variant-preview-ack'
  requestId?: string
  status?: 'ok' | 'error'
  error?: string
}

type SendPreviewOptions = {
  tabId?: number | null
  trackConnection?: boolean
}

export type PreviewConnectionStatus = 'checking' | 'connected' | 'disconnected'

const PREVIEW_ACK_TIMEOUT_MS = 1500

export const useSendPreview = () => {
  const activePage = useActiveWorkspacePage()
  const [previewConnectionStatus, setPreviewConnectionStatus] = useState<PreviewConnectionStatus>('disconnected')
  const [previewConnectionMessage, setPreviewConnectionMessage] = useState<string>('No preview acknowledgment yet.')
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null)
  const lastRequestIdRef = useRef<string | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const activePageRef = useRef(activePage)
  const desiredPreviewRef = useRef<PreviewMessage | null>(null)
  const previewTabIdRef = useRef<number | null>(null)
  const previewPageRevisionRef = useRef<number | null>(null)

  useEffect(() => {
    activePageRef.current = activePage
  }, [activePage])

  const clearAckTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      clearAckTimeout()
    }
  }, [clearAckTimeout])

  useEffect(() => {
    const runtimeMessages = typeof chrome !== 'undefined'
      ? chrome.runtime?.onMessage
      : undefined
    if (!hasBrowserEventListeners(runtimeMessages)) {
      return
    }

    const handleAckMessage = (message: PreviewAckMessage) => {
      if (message.type !== 'variant-preview-ack') {
        return
      }

      if (!message.requestId || message.requestId !== lastRequestIdRef.current) {
        return
      }

      clearAckTimeout()
      setLastCheckedAt(Date.now())
      if (message.status === 'ok') {
        setPreviewConnectionStatus('connected')
        setPreviewConnectionMessage('Preview connected.')
        return
      }

      setPreviewConnectionStatus('disconnected')
      setPreviewConnectionMessage(message.error || 'Preview acknowledgment failed.')
    }

    runtimeMessages.addListener(handleAckMessage)
    return () => {
      runtimeMessages.removeListener(handleAckMessage)
    }
  }, [clearAckTimeout])

  const dispatchPreview = useCallback(async (
    message: PreviewMessage,
    tabId: number,
    trackConnection: boolean,
  ) => {
    const isChromeExtension = typeof chrome !== 'undefined' && chrome?.runtime?.sendMessage;

    if (!isChromeExtension) {
      console.log('Chrome extension APIs not available - running in web page');
      if (trackConnection) {
        setPreviewConnectionStatus('disconnected')
        setPreviewConnectionMessage('Extension APIs are unavailable.')
      }
      return; // Exit early
    }

    try {
      if (trackConnection) {
        clearAckTimeout()
        setPreviewConnectionStatus('checking')
        setPreviewConnectionMessage('Waiting for preview acknowledgment...')
      }

      const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      if (trackConnection) {
        lastRequestIdRef.current = requestId
      }

      await new Promise<void>((resolve, reject) => {
        chrome.runtime.sendMessage({ ...message, requestId, tabId }, () => {
          if (chrome.runtime.lastError) {
            reject(new Error(chrome.runtime.lastError.message))
            return
          }
          resolve()
        })
      })

      if (trackConnection) {
        timeoutRef.current = window.setTimeout(() => {
          if (lastRequestIdRef.current !== requestId) {
            return
          }
          setPreviewConnectionStatus('disconnected')
          setPreviewConnectionMessage('No preview acknowledgment from page.')
          setLastCheckedAt(Date.now())
        }, PREVIEW_ACK_TIMEOUT_MS)
      }
    } catch (error) {
      console.error('Error sending preview message:', error);
      if (trackConnection) {
        setPreviewConnectionStatus('disconnected')
        setPreviewConnectionMessage('Unable to send preview to active tab.')
        setLastCheckedAt(Date.now())
      }
    }
  }, [clearAckTimeout]);

  const sendPreview = useCallback(async (
    message: PreviewMessage,
    options: SendPreviewOptions = {},
  ) => {
    const targetPage = activePageRef.current
    const trackConnection = options.trackConnection ?? message.action !== 'clear'
    const appliedTabId = previewTabIdRef.current
    const tabId = message.action === 'clear'
      ? options.tabId ?? appliedTabId ?? targetPage.tabId
      : options.tabId ?? targetPage.tabId

    if (message.action === 'clear') {
      desiredPreviewRef.current = null
      previewTabIdRef.current = null
      previewPageRevisionRef.current = null

      if (Number.isInteger(tabId)) {
        await dispatchPreview(message, tabId as number, trackConnection)
      }
      return
    }

    desiredPreviewRef.current = message

    if (!Number.isInteger(tabId)) {
      if (trackConnection) {
        setPreviewConnectionStatus('disconnected')
        setPreviewConnectionMessage('No active tab available for preview.')
      }
      return
    }

    if (!targetPage.isVerifiedWorkspacePage) {
      if (trackConnection) {
        setPreviewConnectionStatus('disconnected')
        setPreviewConnectionMessage('Open a verified workspace page to connect the preview.')
      }
      return
    }

    previewTabIdRef.current = tabId as number
    previewPageRevisionRef.current = targetPage.pageRevision

    await dispatchPreview(message, tabId as number, trackConnection)
  }, [dispatchPreview])

  useEffect(() => {
    const desiredPreview = desiredPreviewRef.current
    if (!desiredPreview) return

    const previousTabId = previewTabIdRef.current
    const nextTabId = activePage.tabId

    if (
      previousTabId !== null
      && (
        previousTabId !== nextTabId
        || !activePage.isVerifiedWorkspacePage
      )
    ) {
      void dispatchPreview(
        { ...desiredPreview, action: 'clear' },
        previousTabId,
        false,
      )
      previewTabIdRef.current = null
      previewPageRevisionRef.current = null
    }

    if (!activePage.isVerifiedWorkspacePage || !Number.isInteger(nextTabId)) {
      clearAckTimeout()
      lastRequestIdRef.current = null
      setPreviewConnectionStatus('disconnected')
      setPreviewConnectionMessage('Open a verified workspace page to connect the preview.')
      return
    }

    if (
      previousTabId !== nextTabId
      || previewPageRevisionRef.current !== activePage.pageRevision
    ) {
      previewTabIdRef.current = nextTabId as number
      previewPageRevisionRef.current = activePage.pageRevision
      void dispatchPreview(desiredPreview, nextTabId as number, true)
    }
  }, [
    activePage.isVerifiedWorkspacePage,
    activePage.pageRevision,
    activePage.tabId,
    clearAckTimeout,
    dispatchPreview,
  ])

  const getPageUrl = useCallback(async () => {
    // Check if Chrome extension APIs are available
    const isChromeExtension = typeof chrome !== 'undefined' &&
      chrome?.tabs?.query;

    if (!isChromeExtension) {
      // Fallback to current page URL
      return window.location.href;
    }

    return activePageRef.current.tabUrl || '';
  }, []);

  return {
    sendPreview,
    getPageUrl,
    previewConnectionStatus,
    previewConnectionMessage,
    lastCheckedAt
  };
};
